import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, User, Heart, Menu, X, Search } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { isAuthenticated, logout, isAdmin, user } = useAuth();
  const { cartCount } = useCart();
  const navigate = useNavigate();

  const navLinks = [
    { name: 'Shop', path: '/shop' },
    { name: 'Birthstones', path: '/birthstones' },
    { name: 'Request a Stone', path: '/request-stone' },
    { name: 'About', path: '/about' },
    { name: 'Education', path: '/blog' },
    { name: 'Contact', path: '/contact' },
  ];

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-black">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img
              src="https://customer-assets.emergentagent.com/job_3b35c559-07b5-4fd8-a210-8ba78c0ad2dc/artifacts/thh28hbe_8E8B7D47-8469-4973-B939-8758B2C06966.png"
              alt="The Gem Atelier"
              className="h-16 w-auto"
              data-testid="logo"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className="text-sm tracking-wider uppercase hover:opacity-60 transition-opacity link-underline"
                data-testid={`nav-${link.name.toLowerCase()}`}
              >
                {link.name}
              </Link>
            ))}
          </div>

          {/* Desktop Icons */}
          <div className="hidden md:flex items-center space-x-6">
            <button 
              onClick={() => navigate('/shop')}
              className="hover:opacity-60 transition-opacity"
              data-testid="search-button"
            >
              <Search className="w-5 h-5" />
            </button>

            {isAuthenticated() && (
              <Link 
                to="/wishlist" 
                className="hover:opacity-60 transition-opacity"
                data-testid="wishlist-link"
              >
                <Heart className="w-5 h-5" />
              </Link>
            )}

            <Link 
              to="/cart" 
              className="relative hover:opacity-60 transition-opacity"
              data-testid="cart-link"
            >
              <ShoppingCart className="w-5 h-5" />
              {cartCount > 0 && (
                <span 
                  className="absolute -top-2 -right-2 bg-black text-white text-xs w-5 h-5 rounded-full flex items-center justify-center"
                  data-testid="cart-count"
                >
                  {cartCount}
                </span>
              )}
            </Link>

            {isAuthenticated() ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button 
                    className="hover:opacity-60 transition-opacity"
                    data-testid="user-menu-trigger"
                  >
                    <User className="w-5 h-5" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem onClick={() => navigate('/account')}>My Account</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/orders')}>Orders</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/wishlist')}>Wishlist</DropdownMenuItem>
                  {isAdmin() && (
                    <>
                      <DropdownMenuItem onClick={() => navigate('/admin')}>Admin Dashboard</DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuItem onClick={logout}>Sign Out</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/login')}
                className="border-black text-black hover:bg-black hover:text-white"
                data-testid="login-button"
              >
                Sign In
              </Button>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden hover:opacity-60 transition-opacity"
            data-testid="mobile-menu-button"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="md:hidden border-t border-black bg-white" data-testid="mobile-menu">
          <div className="px-4 py-6 space-y-4">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className="block text-lg tracking-wider uppercase hover:opacity-60 transition-opacity"
                onClick={() => setMobileMenuOpen(false)}
              >
                {link.name}
              </Link>
            ))}
            <div className="pt-4 border-t border-gray-200 space-y-4">
              {isAuthenticated() ? (
                <>
                  <Link
                    to="/account"
                    className="block text-lg hover:opacity-60 transition-opacity"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    My Account
                  </Link>
                  <Link
                    to="/orders"
                    className="block text-lg hover:opacity-60 transition-opacity"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Orders
                  </Link>
                  <Link
                    to="/wishlist"
                    className="block text-lg hover:opacity-60 transition-opacity"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Wishlist
                  </Link>
                  {isAdmin() && (
                    <Link
                      to="/admin"
                      className="block text-lg hover:opacity-60 transition-opacity"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Admin Dashboard
                    </Link>
                  )}
                  <button
                    onClick={() => {
                      logout();
                      setMobileMenuOpen(false);
                    }}
                    className="block text-lg hover:opacity-60 transition-opacity"
                  >
                    Sign Out
                  </button>
                </>
              ) : (
                <Button
                  variant="outline"
                  onClick={() => {
                    navigate('/login');
                    setMobileMenuOpen(false);
                  }}
                  className="w-full border-black text-black hover:bg-black hover:text-white"
                >
                  Sign In
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;